import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AppIcon, Button, IconRefresh, Notice, TabPanel, Tabs } from "@wilco/ui";
import { useState } from "react";
import { formatDate, t, type MessageKey } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { productionApi } from "./api.ts";
import { boardQuery, PRODUCTION } from "./queries.ts";
import type { BoardCard, BoardColumn } from "./types.ts";

const COLUMNS: { id: BoardColumn; title: MessageKey; empty: MessageKey }[] = [
  { id: "WAITING_KIT", title: "line.col.WAITING_KIT", empty: "line.empty.WAITING_KIT" },
  { id: "ON_LINE", title: "line.col.ON_LINE", empty: "line.empty.ON_LINE" },
  { id: "IN_WORK", title: "line.col.IN_WORK", empty: "line.empty.IN_WORK" },
  { id: "DONE_TODAY", title: "line.col.DONE_TODAY", empty: "line.empty.DONE_TODAY" },
];

/**
 * Доска «Лінія» (chmk_floor, line; ВР-15, МР-20): заказы «Серії» в четырёх
 * колонках. На ПК колонки рядом, на телефоне вкладками, по умолчанию первая
 * непустая из «У роботі», «На лінії», «Чекає комплект». Ошибка действия
 * пишется в карточке, а не диалогом.
 */
export function LinePage() {
  const board = useQuery(boardQuery);
  const cards = board.data ?? [];
  const by = (c: BoardColumn) => cards.filter((x) => x.column === c);
  const preferred = (["IN_WORK", "ON_LINE", "WAITING_KIT"] as const).find((c) => by(c).length > 0) ?? "IN_WORK";
  const [tab, setTab] = useState<BoardColumn | null>(null);
  const current = tab ?? preferred;

  const column = (c: (typeof COLUMNS)[number]) => (
    <section className="line-col" aria-labelledby={`col-${c.id}`} key={c.id}>
      <h2 id={`col-${c.id}`} className="desk-group-title line-col-title">
        {t(c.title)} <span className="w-num">{by(c.id).length}</span>
      </h2>
      {by(c.id).length === 0 ? <p className="line-empty w-muted">{t(c.empty)}</p> : null}
      {by(c.id).map((card) => (
        <LineCard key={card.orderId} card={card} />
      ))}
    </section>
  );

  return (
    <div className="page line-page">
      <div className="page-head">
        <h1 className="page-title page-title-icon">
          <AppIcon name="mrp" size={32} />
          {t("line.title")}
        </h1>
        <Button size="icon" variant="ghost" aria-label={t("today.refresh")} onClick={() => void board.refetch()}>
          <IconRefresh />
        </Button>
      </div>
      {board.error ? <Notice tone="danger" title={errorNotice(board.error).title} className="stock-notice" /> : null}
      <div className="line-grid">{COLUMNS.map(column)}</div>
      <div className="line-tabs">
        <Tabs<BoardColumn>
          value={current}
          onValueChange={setTab}
          label={t("line.title")}
          items={COLUMNS.map((c) => ({ value: c.id, label: t(c.title), count: by(c.id).length }))}
        >
          {COLUMNS.map((c) => (
            <TabPanel key={c.id} value={c.id} className="queue">
              {by(c.id).length === 0 ? <p className="line-empty w-muted">{t(c.empty)}</p> : null}
              {by(c.id).map((card) => (
                <LineCard key={card.orderId} card={card} />
              ))}
            </TabPanel>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

function LineCard({ card }: { card: BoardCard }) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn() });
  const act = (fn: () => Promise<unknown>, ok: (r: unknown) => NoticeState | null) =>
    run.mutate(fn, {
      onSuccess: (r) => {
        setNotice(ok(r));
        void queryClient.invalidateQueries({ queryKey: PRODUCTION });
      },
      onError: (e) => setNotice(errorNotice(e)),
    });
  const onLine = card.kitTotal - card.kitShort;
  const r = card.route;

  return (
    <article className="line-card w-plate w-cut" data-late={card.late ? "" : undefined} data-cut-bleed="">
      <div className="line-card-head">
        <Link to="/production/orders/$id" params={{ id: card.orderId }} className="line-card-number w-num">
          {card.number}
        </Link>
        <span className={card.late ? "queue-late" : "w-muted"}>
          {formatDate(card.start)}
          {card.late ? ` · ${t("today.late")}` : ""}
        </span>
      </div>
      <div className="line-card-product">
        {`${card.productName}${card.version ? ` ${card.version}` : ""} × ${String(card.qty)}`}
        {card.origin ? <span className="w-muted">{` · ${card.origin}`}</span> : null}
      </div>
      {card.column === "WAITING_KIT" ? (
        <>
          <div className="progress" role="progressbar" aria-label={t("line.kitProgress")} aria-valuemin={0} aria-valuemax={card.kitTotal} aria-valuenow={onLine}>
            <span className="progress-bar" style={{ transform: `scaleX(${String(card.kitTotal ? onLine / card.kitTotal : 0)})` }} />
          </div>
          <p className="w-muted line-card-meta">
            {t("line.notOnLine", { short: card.kitShort, total: card.kitTotal })}
            {card.kitRequest ? ` · ${t("line.kitRequest", { request: card.kitRequest })}` : ""}
          </p>
        </>
      ) : null}
      {card.firstSerial ? (
        <p className="w-num line-card-meta">{card.firstSerial === card.lastSerial ? card.firstSerial : `${card.firstSerial} … ${card.lastSerial ?? ""}`}</p>
      ) : null}
      {r ? (
        <p className="line-card-meta">
          {[...r.steps.map((s) => `${s.name} ${String(s.count)}`), t("line.routeDone", { count: r.done })].join(" · ")}
        </p>
      ) : null}
      {r && r.quarantined.length > 0 ? <p className="line-card-alert">{t("line.quarantine", { serials: r.quarantined.join(", ") })}</p> : null}
      {r?.stuck.map((s) => (
        <p key={s.serial} className="line-card-alert">
          {t("line.stuck", { serial: s.serial, step: s.step, hours: s.hours })}
        </p>
      ))}
      {notice ? <Notice tone={notice.tone} title={notice.title} /> : null}
      <div className="line-card-actions">
        {card.column === "WAITING_KIT" && !card.kitRequest ? (
          <Button disabled={run.isPending} onClick={() => act(() => productionApi.kit(card.orderId), () => ({ tone: "success", title: t("mo.kitIssued") }))}>
            {t("mo.issueKit")}
          </Button>
        ) : null}
        {card.column === "ON_LINE" ? (
          // «Почати» в wilco это серийники с маршрутом и этикетки (ВР-06).
          <Button variant="primary" disabled={run.isPending} onClick={() => act(() => productionApi.serials(card.orderId), () => null)}>
            {t("line.start")}
          </Button>
        ) : null}
        {card.column === "IN_WORK" ? (
          <Button
            variant="primary"
            disabled={run.isPending}
            onClick={() => act(() => productionApi.packReady(card.orderId), (s) => ({ tone: "success", title: t("mo.packed", { serials: (s as string[]).join(", ") }) }))}
          >
            {t("mo.packReady")}
          </Button>
        ) : null}
      </div>
    </article>
  );
}
